import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import Button from 'antd/es/button';
import notification from 'antd/es/notification';
import { ColumnProps } from 'antd/es/table/Column';
import message from 'antd/es/message';
import Table from 'antd/es/table';
import {
    ErrorBoundary,
    BasicFilterDropdown,
    FilterIcon,
    DateFilterDropdown,
    dateFilter,
} from '@allenai/varnish';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import {
    FilterValue,
    SorterResult,
    TableCurrentDataSource,
    TablePaginationConfig,
} from 'antd/es/table/interface';
import Input from 'antd/es/input';

import { Properties, hasProperties, UiWidgets } from '../data';
import { columnRender, PageHeader, TableSansTitleStyle, ScreenReaderOnly } from '../components';
import { deleteAsset, getAssetsOfCategory } from '../api';
import { components } from '../../build/openapiTypes';
import {
    UiSchemaKey,
    tableWideFilter,
    yesNoFilter,
    getCategoryAndAssetDetails,
} from '../utils/util';
import { TableActions } from '../components/TableActions';

const { Search } = Input;

interface Params {
    category: string;
}

const CategoryImpl = ({ match, history, location }: RouteComponentProps<Params>) => {
    const { categoryData, assetData, assetSchema, assetNameFromType } = getCategoryAndAssetDetails(
        match.params.category
    );

    // grab ts type for the asset
    // omitting string because we want only objects
    type Asset = Omit<components['schemas'][typeof assetNameFromType], string>;

    const [isLoading, setIsLoading] = useState(false);
    const [filteredInfo, setFilteredInfo] = useState<{ [key: string]: any }>({});
    const [searchValue, setSearchValue] = useState<string>();
    const areAnyColumnsFiltered = () =>
        filteredInfo ? Object.values(filteredInfo).some((val) => Boolean(val)) : false;
    const currentFilteredCount = () => {
        if (areAnyColumnsFiltered()) {
            return filteredTotal;
        } else {
            return assets.length;
        }
    };
    const [filteredTotal, setFilteredTotal] = useState(0);

    // data fetched from backend
    const [dataSource, setDataSource] = useState<Array<Asset>>([]);
    // data that may or may not be filtered (used for table-wide searching)
    const [assets, setAssets] = useState<Array<Asset>>([]);

    // determines whether there are any table wide filters or column specific filters active
    const areResultsFiltered =
        dataSource.length !== assets.length ||
        (areAnyColumnsFiltered() && filteredTotal !== assets.length);

    // go get assets
    useEffect(() => {
        loadAssets();
    }, []);

    const loadAssets = () => {
        setIsLoading(true);
        getAssetsOfCategory<Asset>(match.params.category)
            .then((r) => {
                setDataSource(r);
                setAssets(r);
            })
            .catch((e) => message.error(`Error loading data: ${e.message}`))
            .finally(() => setIsLoading(false));
    };

    // set up table columns
    const columns: ColumnProps<Asset>[] = [];
    if (hasProperties(assetSchema)) {
        columns.push(
            ...Object.entries(assetSchema.properties)
                .filter(([_k, v]) => {
                    const widget = v[UiSchemaKey.WidgetTable] || v[UiSchemaKey.Widget];
                    return widget !== UiWidgets.Omit;
                })
                .map(([k, v]) => {
                    const widget = v[UiSchemaKey.WidgetTable] || v[UiSchemaKey.Widget];
                    const widgetProps =
                        v[UiSchemaKey.WidgetTableProps] || v[UiSchemaKey.WidgetProps];
                    return {
                        title: v.title,
                        dataIndex: k,
                        key: k,
                        width: widget === UiWidgets.Truncated ? 200 : 100,
                        render: columnRender(v, UiSchemaKey.WidgetTable),
                        filterDropdown:
                            widget === UiWidgets.Date || widget === UiWidgets.DateTime
                                ? (props: any) => (
                                      <DateFilterDropdown
                                          {...props}
                                          dateFormat={widgetProps?.format}
                                      />
                                  )
                                : BasicFilterDropdown,
                        filterIcon: FilterIcon,
                        onFilter: columnFilter(k, v),
                        filteredValue: filteredInfo[k] ?? null,
                        sorter: (a: any, b: any) =>
                            (a[k] || false).toString().localeCompare((b[k] || false).toString()),
                    };
                })
        );
    }
    // action button
    columns.push({
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        width: 65,
        render: (_: any, record: any) => {
            const assetLabel = assetData.label;
            const recordId = record[categoryData.key];
            return (
                <TableActions
                    assetLabel={assetLabel}
                    recordId={recordId}
                    asset={match.params.category}
                    onDelete={() =>
                        deleteAsset(assetData.category, recordId)
                            .then(loadAssets)
                            .then(() => {
                                notification.open({
                                    message: 'Success',
                                    description: `Deleted ${assetLabel}: ${recordId}`,
                                });
                            })
                    }
                />
            );
        },
    });

    const handleTableChange = (
        _pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        _sorter: SorterResult<any> | SorterResult<any>[],
        extra: TableCurrentDataSource<any>
    ) => {
        setFilteredInfo(filters);
        setFilteredTotal(extra.currentDataSource.length);
    };

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchText = e.target.value;
        setSearchValue(searchText);
        if (!searchText) {
            // reset table data
            setAssets(dataSource);
        } else {
            // filter on the full original data from the backend and
            // set the table's current assets
            setAssets(dataSource.filter(tableWideFilter(searchText)));
        }
    };
    return (
        <>
            <PageHeader
                iconName={categoryData.iconName}
                iconType={categoryData.iconType}
                label={categoryData.label}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    aria-label={`Add a new ${assetData.label}`}
                    onClick={() => history.push(`${location.pathname}/new`)}>
                    Add a new {assetData.label}
                </Button>
            </PageHeader>
            <TopLevelTableActionContainer>
                <TableSearch
                    placeholder="input table search text"
                    value={searchValue}
                    onChange={handleSearchInputChange}
                />
                {areResultsFiltered && (
                    <ResultCount>
                        Showing {currentFilteredCount()} of {dataSource.length}
                    </ResultCount>
                )}
                {
                    <ClearFiltersButton
                        onClick={() => {
                            setFilteredInfo({});
                            setAssets(dataSource);
                            setSearchValue('');
                        }}
                        disabled={!areResultsFiltered}>
                        Clear Filters
                    </ClearFiltersButton>
                }
            </TopLevelTableActionContainer>

            <CategoryTable
                title={() => (
                    <ScreenReaderOnly>{`Table of ${categoryData.label}`}</ScreenReaderOnly>
                )}
                loading={isLoading}
                rowKey={categoryData.key}
                scroll={{ x: `${(columns.length - 1) * 200}px` }}
                dataSource={assets}
                columns={columns}
                pagination={{
                    hideOnSinglePage: true,
                    defaultPageSize: 5,
                    pageSizeOptions: ['5', '10', '20', '50', '100'],
                    showSizeChanger: true,
                }}
                onChange={handleTableChange}
            />
        </>
    );
};

export const CategoryTable = styled(Table)`
    ${TableSansTitleStyle}
`;

const columnFilter = (columnName: string, properties: Properties) => {
    switch (properties[UiSchemaKey.Widget]) {
        case UiWidgets.YesNo:
            return yesNoFilter(columnName);
        case UiWidgets.Date:
        case UiWidgets.DateTime:
            return dateFilter(columnName);
        default:
            return (value: string | number | boolean, record: any) =>
                (record[columnName] || false)
                    .toString()
                    .toLowerCase()
                    .includes(value.toString().toLowerCase());
    }
};

export const Category = (props: RouteComponentProps<Params>) => {
    return (
        <ErrorBoundary key={props.match.params.category}>
            <CategoryImpl {...props} />
        </ErrorBoundary>
    );
};

const TopLevelTableActionContainer = styled.div`
    display: grid;
    grid-template: 'search filterCount actionBtn' 1fr / 3fr 1fr min-content;
    grid-gap: ${({ theme }) => theme.spacing.sm};
    align-items: baseline;
`;

const ClearFiltersButton = styled(Button)`
    grid-area: actionBtn;
`;
const TableSearch = styled(Search)`
    width: 100%;
    grid-area: search;
    margin-bottom: ${({ theme }) => theme.spacing.md};
    &&&& .ant-input-group-addon {
        left: auto;
    }
    .ant-input-suffix {
        margin-right: ${({ theme }) => theme.spacing.lg};
    }
`;

const ResultCount = styled.p`
    grid-area: filterCount;
`;
